import { Flex, Text, Box, Button } from "@chakra-ui/react";
import { Section, sectionSpacingY } from "components/section";
import { useHeaderHeight } from "contexts/header-height";
import React, { useEffect, useRef, useState } from "react";
import { Chip } from "ui/chips";
import { FilterButton } from "./filter-button";
import { SearchInput } from "./search-input";

type SimpleCatalogProps<T> = {
  /** All data available in the catalog, e.g. pages. */
  data: T[];
  /** Categories to list in the sidebar. */
  categories: string[];
  categoriesHeading?: string;
  searchPlaceholder?: string;

  /** Function used to filter records when a category in the sidebar is clicked. */
  onCategoryFilter: (record: T, currentCategory: string) => boolean;
  /** Function used to filter records when something is typed into the search input. */
  onSearch: (record: T, search: string) => boolean;
  /**
   * Function used to render the list of items in the catalog, e.g. rows or cards.
   * Following pattern/naming convention from [the React docs](
   * https://react.dev/reference/react/cloneElement#passing-data-with-a-render-prop).
   * */
  renderItems: (filteredItems: T[]) => React.ReactNode;
};

/**
 * A basic catalog UI element that can handle filtering and rendering of arbitrary data types,
 * as defined by the parent component. **Does not** use any Algolia search components.
 */
export function SimpleCatalog<T>({
  data,
  categoriesHeading = "Categories",
  categories,
  searchPlaceholder = "Search...",
  onCategoryFilter,
  onSearch,
  renderItems,
}: SimpleCatalogProps<T>) {
  const { headerHeight } = useHeaderHeight();
  const [currentCategory, setCurrentCategory] = useState("");
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<T[]>(data);
  const dictionaryContainer = useRef<HTMLDivElement>(null);

  const clear = () => {
    setCurrentCategory("");
    setSearch("");
    setResults(data);
  };

  // When the current category changes, filter records using the passed in filtering function
  useEffect(() => {
    setResults(data.filter((item) => onCategoryFilter(item, currentCategory)));
  }, [onCategoryFilter, currentCategory, data]);

  // When the search value changes, filter records using the passed in filtering function
  useEffect(() => {
    if (search === "") {
      setResults(data);
    } else {
      setResults(data.filter((item) => onSearch(item, search)));
    }
  }, [onSearch, search, data]);

  // Scroll catalog to top when search begins or a category is clicked
  useEffect(() => {
    if (
      dictionaryContainer?.current &&
      window.scrollY !== dictionaryContainer.current.offsetTop &&
      window.scrollY > 0 // Don't scroll on page load
    ) {
      window.scrollTo({ top: dictionaryContainer.current.offsetTop - headerHeight, behavior: "smooth" });
    }
  }, [currentCategory, data, headerHeight]);

  return (
    <Section my={0}>
      <Flex
        ref={dictionaryContainer}
        align="start"
        direction={{ base: "column", lg: "row" }}
        pos="relative"
        pb={sectionSpacingY}
      >
        <Flex
          display={{ base: "none", lg: "flex" }}
          direction="column"
          width="300px"
          pos="sticky"
          top={`${headerHeight}px`}
        >
          <Flex direction="column" w="80%" mt={5}>
            <Text fontWeight="semibold" p={2.5}>
              {categoriesHeading}
            </Text>

            <Flex direction="column" maxH={`calc(100vh - ${headerHeight + 40}px)`} w="100%" overflow="auto">
              {categories.map((category) => {
                return (
                  <FilterButton
                    key={category}
                    isActive={currentCategory === category}
                    onClick={() => {
                      if (currentCategory !== category) {
                        setCurrentCategory(category);
                      }
                    }}
                  >
                    {category}
                  </FilterButton>
                );
              })}
            </Flex>
          </Flex>
        </Flex>

        <Flex align="start" direction="column" flex={1} w="100%">
          <Box pos="sticky" top={`${headerHeight}px`} w="100%" zIndex={100}>
            <SearchInput
              value={search}
              placeholder={searchPlaceholder}
              onChange={(event) => {
                // Remove selected category when search changes
                if (currentCategory !== "") {
                  setCurrentCategory("");
                }

                setSearch(event.target.value);
              }}
              onClear={() => {
                setSearch("");
              }}
            />

            {currentCategory && (
              <Flex bg="white" pt={5} w="100%">
                <Chip variant="gray" onCloseClick={clear}>
                  {currentCategory}
                </Chip>
              </Flex>
            )}

            <Box
              mx={{ base: 0, xl: -8 }}
              w={`calc(100% + ${16})`}
              h={5}
              bgGradient="linear(#fff, 90%, rgba(255, 255, 255, 0))"
            />
          </Box>

          {results.length > 0 ? (
            // Render the current set of filtered results using the passed in rendering function
            renderItems(results)
          ) : (
            <Box mt={7}>
              <Text textStyle="h6">No results found</Text>
              <Flex as={Text} align="center" gap={1} mt={4}>
                <Button
                  variant="unstyled"
                  fontSize="lg"
                  textDecoration="underline"
                  textUnderlineOffset={3}
                  _hover={{ color: "grass " }}
                  onClick={clear}
                >
                  Reset your filters
                </Button>{" "}
                <Text as="span" fontSize="lg">
                  to continue exploring.
                </Text>
              </Flex>
            </Box>
          )}
        </Flex>
      </Flex>
    </Section>
  );
}
